import { UseFormReturn } from "react-hook-form";

import { useGlobalData } from "../../GlobalDataProvider";
import { IModuleRound, useRound } from "../../../modules/roundHandler";
import { IGlobalData, MapTooltip } from "../../../types/globalData";
import Funktionskontroller from "./Funktionskontroller";
import BFLNote from "./BFLNote";
import { bfv_J12 } from "../../../modules/Beräkningsmoduler/BFV_J12";
import DebugInfo from "./DebugInfo";
import SelectEditor from "../../Global/Editors/SelectEditor";
import NumberEditor from "../../Global/Editors/NumberEditor";
import { nameof } from "../../Global/Utils";
import { IJ12_BFData } from "../../../types/BFDatas";
import DriftOptimeringar from "./DriftOptimeringar";
import { useEffect } from "react";
import { useBflHandler } from "../../../modules/Beräkningsmoduler/bflHandler";
import { BFLType } from "../../../types/DBTables";
import ErrorInfo from "./ErrorInfo";
import { IModuleVärmeEl } from "../../../types/ModuleDatas";
import { Card } from "react-bootstrap";
import StaticBox from "../../Global/StaticBox";
import { Value } from "devextreme-react/range-selector";
import { logDebugSilently } from "../../../modules/logger";

interface IProps {
  module: IModuleRound;
  formHook: UseFormReturn;
}

const BFV_J12 = ({ module, formHook }: IProps) => {
  const roundHandler = useRound();
  const global = useGlobalData();
  const bflHandler = useBflHandler();

  logDebugSilently("Render BFV_J12@" + new Date());
  const moduleData: IModuleVärmeEl = module.data;

  const bimHandler = roundHandler.getBimHandler();
  const watch = formHook.watch;

  // Callback version of watch.  It's your responsibility to unsubscribe when done.
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      //const bfJRes = bfv_J11().getResult(module, bimHandler, roundHandler, global.data as IGlobalData);
      if (name === "data." + nameof<IJ12_BFData>("nyttVärdeVärmestopp")) {
        //Update from Form
        const formData: any = formHook.getValues();
        const bflResult = bflHandler.prepareData(module, BFLType.BFV_J12, formData);

        module.bfs.BFV_J12 = bflResult;
        formHook.reset(bflResult);
        logDebugSilently("BFV_J12.watch");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, global.data, formHook, roundHandler, module, bflHandler]);

  formHook.watch(); //Rerender for any changes in inputs

  const harAngettNyInställningVärmestopp = formHook.watch("data.harAngettNyInställningVärmestopp");
  const nyttVärdeVärmestopp = formHook.watch("data.nyttVärdeVärmestopp");

  const formData: any = formHook.getValues();

  //if (module.bfs.BFV_J12 && formData.data) module.bfs.BFV_J12.data = formData.data;
  const bm = bfv_J12().getResult(module, bimHandler, roundHandler, global.data as IGlobalData, formData);

  const funktionsKontroller = bm.funktionsKontroller;
  const driftOptimeringar = bm.driftOptimeringar;

  const systemText = global.beräkningsflikarText.beskr_BFV_J12;
  const värmestoppInställningsvärde = new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 1 }).format(moduleData.värmestoppInställningsvärde);

  return (
    <>
      <Card className="mb-3">
        <Card.Header>{systemText?.title}</Card.Header>
        <Card.Body>
          <div dangerouslySetInnerHTML={{ __html: systemText?.body as string }} />
          <Funktionskontroller bfName="BFV_J12" formHook={formHook} funktionsKontroller={funktionsKontroller} />
          {moduleData.harÖverordnatVärmestopp === "Ja" && moduleData.kontrollVärmestoppFungerar === "Ja" && (
            <Card className="mb-3">
              <Card.Header>Manuella kontroller</Card.Header>
              <Card.Body>
                <div className="row overflow-hidden gy-0">
                  <div className="col-lg-6">
                    <SelectEditor
                      label="Har angivit nytt inställningsvärde för värmestopp"
                      formHook={formHook}
                      fieldName={"data." + nameof<IJ12_BFData>("harAngettNyInställningVärmestopp")}
                      tooltipId={nameof<MapTooltip>("BFVJ12_harAngettNyInställningVärmestopp")}
                      items={["", "Ja", "Nej"]}
                      options={{ required: "Måste göra ett val" }}
                    ></SelectEditor>
                  </div>
                  <div className="col-lg-6">
                    <NumberEditor
                      step={0.1}
                      min={10}
                      max={25}
                      isDisabled={harAngettNyInställningVärmestopp !== "Ja"}
                      label={"Nytt inställningsvärde värmestopp (Befintligt värde: " + värmestoppInställningsvärde + ")"}
                      fieldName={"data." + nameof<IJ12_BFData>("nyttVärdeVärmestopp")}
                      tooltipId={nameof<MapTooltip>("BFVJ12_nyttVärdeVärmestopp")}
                      formHook={formHook}
                      options={{
                        validate: {
                          required: (value: any) => (value || value === 0 ? undefined : "Måste anges"),
                          min: (value: any) => (value && value < 10 ? "> 10 ℃" : undefined),
                          max: (value: any) => (value && value > 25 ? "< 25 ℃" : undefined),
                        },
                      }}
                    ></NumberEditor>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          <DriftOptimeringar bfName="BFV_J12" formHook={formHook} driftOptimeringar={driftOptimeringar} />
          <BFLNote formHook={formHook} />
          <ErrorInfo info={bm.error} />
          <DebugInfo info={bm.debug} />
        </Card.Body>
      </Card>
    </>
  );
};

export default BFV_J12;
